/* engine.ts
 *
 * One place that reads scroll position and writes CSS custom properties.
 * Effects live in CSS — they consume the vars this engine sets:
 *
 *   'view'  -> --p   0 when the element's top enters the viewport bottom,
 *                    1 when its bottom leaves the viewport top
 *   'enter' -> --p   0 when the element's top enters the viewport bottom,
 *                    1 once it has travelled `span` viewport-heights upward
 *   'pin'   -> --hx  horizontal scrub for a sticky section; the engine also
 *                    sizes the section so vertical scroll maps 1:1 to the
 *                    track's overflow width
 *
 * Measurements are cached and only recomputed on resize/refresh, so the
 * per-frame work is pure math + a few style writes.
 */

export type TrackMode = 'view' | 'enter' | 'pin' | 'scrub';

export interface TrackOptions {
    mode?: TrackMode;
    /* 'enter' mode: fraction of the viewport the element travels to reach p=1 */
    span?: number;
    /* element that slides horizontally in 'pin' mode */
    pinTrack?: HTMLElement;
    /* pin only applies while this returns true (checked on refresh) */
    when?: () => boolean;
    /* notified whenever p changes (used by the shader for its parallax uniform) */
    onUpdate?: (p: number) => void;
    /* scroll ratio (slowing speed by expanding height) */
    ratio?: number;
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

class Track {
    readonly el: HTMLElement;
    readonly opts: TrackOptions;

    private top = 0;
    private height = 0;
    private overflow = 0;   // pin mode: how many px the track out-sizes the viewport
    private lastP = -1;

    constructor(el: HTMLElement, opts: TrackOptions) {
        this.el = el;
        this.opts = opts;
    }

    /* pin sections change their own height, so sizing happens before measuring */
    presize(vw: number, vh: number) {
        if (this.opts.mode === 'pin' && this.opts.pinTrack) {
            this.el.style.height = 'auto';
            this.overflow = 0;

            if (this.opts.when && !this.opts.when()) {
                this.opts.pinTrack.style.setProperty('--hx', '0px');
                return;
            }

            this.overflow = Math.max(0, this.opts.pinTrack.scrollWidth - vw);

            if (this.overflow > 0) {
                this.el.style.height = `${vh + this.overflow}px`;
            }
        } else if (this.opts.ratio) {
            this.el.style.height = 'auto';
            if (this.opts.when && !this.opts.when()) {
                return;
            }
            this.el.style.height = `${vh + Math.round(vh / this.opts.ratio)}px`;
        }
    }

    measure(scrollY: number) {
        const rect = this.el.getBoundingClientRect();
        this.top = rect.top + scrollY;
        this.height = rect.height;
    }

    update(scrollY: number, vh: number) {
        let p = 0;

        switch (this.opts.mode ?? 'view') {
            case 'view':
                p = (scrollY + vh - this.top) / (vh + this.height);
                break;
            case 'enter':
                p = (scrollY + vh - this.top) / (vh * (this.opts.span ?? 0.6));
                break;
            case 'pin':
                p = this.overflow > 0 ? (scrollY - this.top) / this.overflow : 0;
                break;
            case 'scrub':
                if (this.opts.when && !this.opts.when()) {
                    p = 0;
                } else {
                    p = this.height > vh ? (scrollY - this.top) / (this.height - vh) : 0;
                }
                break;
        }

        p = clamp01(p);
        if (Math.abs(p - this.lastP) < 0.0005) return;
        this.lastP = p;

        if (this.opts.mode === 'pin') {
            this.opts.pinTrack?.style.setProperty('--hx', `${(-p * this.overflow).toFixed(1)}px`);
        } else {
            this.el.style.setProperty('--p', p.toFixed(4));
        }

        this.opts.onUpdate?.(p);
    }
}

export class ScrollEngine {

    private tracks: Track[] = [];
    private smoothVel = 0;
    private lastWrittenVel = -10;

    add(el: HTMLElement, opts: TrackOptions = {}): void {
        this.tracks.push(new Track(el, opts));
    }

    /* re-measure everything; call on init, resize, and after fonts swap in */
    refresh(scrollY: number): void {
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        for (const t of this.tracks) t.presize(vw, vh);
        for (const t of this.tracks) t.measure(scrollY);
        this.tick(scrollY, 0);
    }

    /* called every animation frame with Lenis' animated scroll + velocity */
    tick(scrollY: number, velocity: number): void {
        const vh = window.innerHeight;

        for (const t of this.tracks) t.update(scrollY, vh);

        // ease velocity toward the live value so marquee skew settles softly
        const target = Math.max(-1, Math.min(1, velocity / 30));
        this.smoothVel += (target - this.smoothVel) * 0.08;

        if (Math.abs(this.smoothVel - this.lastWrittenVel) > 0.002) {
            this.lastWrittenVel = this.smoothVel;
            document.documentElement.style.setProperty('--vel', this.smoothVel.toFixed(3));
        }
    }
}
